/**
 * Shape, in the space of a word.
 *
 * A sparkline is the only chart in the kit that is allowed to appear inside a
 * row, and the price of that placement is that it answers exactly one question:
 * which way did this go. It carries no axis, no gridline and no label, because
 * the figures sitting beside it are the level — a sparkline that implies a
 * level is a chart that lies at 40px wide.
 *
 * Two decisions are load-bearing. The first is that a series of fewer than two
 * points renders NOTHING: one point has no shape, and a single dot on a
 * baseline reads as a measurement that fell to the floor. The second is that
 * the caller captions its own point count through
 * `cresultcardSparklinePointCount`, so a "12 days" caption can never sit beside
 * a line drawn from eleven — or from nothing at all.
 *
 * The end marker is HTML rather than an SVG circle on purpose: the plot is
 * stretched with `preserveAspectRatio="none"` so it fills any column, and a
 * circle inside that box would arrive as an ellipse.
 */
"use client";
import type { ReactElement } from "react";
import {
  CRC_CORE_BACKGROUND_COLOR,
  CRC_FIGURE_SIZE,
  CRC_TONE_COLOR,
} from "./cresultcard.tokens";
import {
  CresultcardFigureScale,
  CresultcardSparklineScale,
} from "./cresultcard.types";
import type { CresultcardTone } from "./cresultcard.types";

/** Below this there is no shape to draw, only a dot pretending to be one. */
export const CRC_SPARKLINE_MIN_POINTS = 2;

/** The plot's own coordinate space. Stretched to the column at paint time. */
const SPARK_VIEW_WIDTH = 100;
const SPARK_VIEW_HEIGHT = 32;
/** Half the stroke, so the extremes are not clipped by the viewBox edge. */
const SPARK_INSET = 2;
const SPARK_STROKE_WIDTH = 1.5;
const SPARK_FILL_OPACITY = 0.16;
/** A flat series sits on the mid-line: on the floor it would read as a crash. */
const FLAT_SERIES_LEVEL = 0.5;
const PATH_PRECISION = 2;

const PATH_MOVE = "M";
const PATH_LINE = "L";
const PATH_CLOSE = "Z";
const PATH_JOIN = " ";

/** The stroke must not thicken when the box is stretched to the column. */
const NON_SCALING_STROKE = "non-scaling-stroke";

const MARKER_DIAMETER = 5;
/** The core plate, so the dot reads as sitting ON the curve, not through it. */
const MARKER_RING_WIDTH = 1.5;
/**
 * How far the marker reaches past the point it sits on: half the dot plus its
 * ring. The marker used to be centred on the curve's last point, which is the
 * plot's own trailing edge — so it painted 4px OUTSIDE the sparkline's box and a
 * 72px cell measured 75px of scroll width across six rows at once. The graphic
 * includes the marker, so the box has to include it: when the marker is drawn,
 * the plot gives up exactly this much on each side it can reach and the marker
 * lands on the curve rather than beyond it.
 */
const MARKER_REACH = MARKER_DIAMETER / 2 + MARKER_RING_WIDTH;
const NO_MARKER_REACH = 0;

/**
 * The three heights. `Inline` and `Face` are steps off the figure ramp so a
 * sparkline never out-measures the numeral it sits beside; `Block` carries the
 * spec's own 56px floor, which is the height below which a full-width curve
 * stops being readable as a curve.
 */
const SPARKLINE_BLOCK_FLOOR = 56;
const SPARKLINE_HEIGHT: Record<CresultcardSparklineScale, number> = {
  [CresultcardSparklineScale.Inline]:
    CRC_FIGURE_SIZE[CresultcardFigureScale.Row],
  [CresultcardSparklineScale.Face]:
    CRC_FIGURE_SIZE[CresultcardFigureScale.Detail],
  [CresultcardSparklineScale.Block]: SPARKLINE_BLOCK_FLOOR,
};

interface SparklineCoordinate {
  readonly x: number;
  readonly y: number;
}

interface SparklineGeometry {
  readonly line: string;
  readonly area: string;
  readonly end: SparklineCoordinate;
}

function sparklineCoordinates(
  points: readonly number[],
): readonly SparklineCoordinate[] {
  const lowest = Math.min(...points);
  const highest = Math.max(...points);
  const span = highest - lowest;
  const lastIndex = points.length - 1;
  const plotHeight = SPARK_VIEW_HEIGHT - SPARK_INSET * 2;

  return points.map((point, index): SparklineCoordinate => {
    const level = span === 0 ? FLAT_SERIES_LEVEL : (point - lowest) / span;
    return {
      x: (index / lastIndex) * SPARK_VIEW_WIDTH,
      y: SPARK_INSET + (1 - level) * plotHeight,
    };
  });
}

function sparklineGeometry(points: readonly number[]): SparklineGeometry {
  const coordinates = sparklineCoordinates(points);
  const line = coordinates
    .map((coordinate, index): string => {
      const command = index === 0 ? PATH_MOVE : PATH_LINE;
      return `${command}${coordinate.x.toFixed(PATH_PRECISION)} ${coordinate.y.toFixed(PATH_PRECISION)}`;
    })
    .join(PATH_JOIN);
  const floor = `${PATH_LINE}${String(SPARK_VIEW_WIDTH)} ${String(SPARK_VIEW_HEIGHT)} ${PATH_LINE}0 ${String(SPARK_VIEW_HEIGHT)} ${PATH_CLOSE}`;

  return {
    line,
    area: `${line} ${floor}`,
    end: coordinates[coordinates.length - 1],
  };
}

/**
 * The number of points the sparkline actually plotted — zero when the series
 * was too short to draw. A caption built from `points.length` would claim a
 * line that is not on the screen.
 */
export function cresultcardSparklinePointCount(
  points: readonly number[],
): number {
  return points.length < CRC_SPARKLINE_MIN_POINTS ? 0 : points.length;
}

interface CresultcardSparklineProps {
  readonly points: readonly number[];
  readonly tone: CresultcardTone;
  readonly scale: CresultcardSparklineScale;
  /** The area under the curve. Off for a bare trace inside a dense row. */
  readonly filled: boolean;
  /** A dot on the last reading, for "where it ended up" beside a figure. */
  readonly endpointMarker?: boolean;
}

export function CresultcardSparkline({
  points,
  tone,
  scale,
  filled,
  endpointMarker = false,
}: CresultcardSparklineProps): ReactElement | null {
  if (points.length < CRC_SPARKLINE_MIN_POINTS) {
    return null;
  }

  const color = CRC_TONE_COLOR[tone];
  const geometry = sparklineGeometry(points);
  const height = SPARKLINE_HEIGHT[scale];
  const reach = endpointMarker ? MARKER_REACH : NO_MARKER_REACH;
  // The plot's own box, once the marker's reach has been taken out of it. The
  // marker's centre is then placed in that box's coordinates, in pixels, so
  // both axes are exact rather than a percentage of the wrong box.
  const plotHeight = height - reach * 2;
  const markerTop =
    reach + (geometry.end.y / SPARK_VIEW_HEIGHT) * plotHeight;

  return (
    <span
      aria-hidden
      data-el="Cresultcard/Sparkline/Root"
      style={{
        position: "relative",
        display: "block",
        boxSizing: "border-box",
        width: "100%",
        height,
        minWidth: 0,
        paddingBlock: reach,
        paddingInlineEnd: reach,
      }}
    >
      <svg
        preserveAspectRatio="none"
        style={{ display: "block", width: "100%", height: "100%" }}
        viewBox={`0 0 ${String(SPARK_VIEW_WIDTH)} ${String(SPARK_VIEW_HEIGHT)}`}
      >
        {filled && (
          <path
            d={geometry.area}
            fill={color}
            fillOpacity={SPARK_FILL_OPACITY}
            stroke="none"
          />
        )}
        <path
          className="cresultcard__path"
          d={geometry.line}
          fill="none"
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={SPARK_STROKE_WIDTH}
          vectorEffect={NON_SCALING_STROKE}
        />
      </svg>
      {endpointMarker && (
        <span
          style={{
            position: "absolute",
            insetInlineStart: `calc(100% - ${String(MARKER_REACH)}px)`,
            insetBlockStart: markerTop,
            width: MARKER_DIAMETER,
            height: MARKER_DIAMETER,
            borderRadius: MARKER_DIAMETER,
            backgroundColor: color,
            boxShadow: `0 0 0 ${String(MARKER_RING_WIDTH)}px ${CRC_CORE_BACKGROUND_COLOR}`,
            transform: "translate3d(-50%, -50%, 0)",
          }}
        />
      )}
    </span>
  );
}
